import { History, Play, RefreshCw, RotateCcw, Search } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from '@/lib/scopedParams';
import { api } from '../../../api';
import { RUN_STATUSES, type BackfillReport, type DomainOperation, type PostOperationRun, type PostOperationRunStatus, type PostOperationStep } from '../../../api/areas/postOperations';
import { Badge, type Tone } from '../../../components/Badge';
import { Callout, DataTable, Field, FilterBar, Section, type Column } from '../../../components/blocks';
import { EntityRef } from '../../../components/EntityRef';
import { PageHeader, useDocumentTitle } from '../../../components/PageHeader';
import { EmptyState, ErrorState, Loading } from '../../../components/States';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { useApi } from '../../../lib/useApi';
import { ActionDialog, FormActions, MfaHint, SelectField, SuccessNote, When } from '../ui';
import { useWrite, WriteError } from './shared';

const RUN_TONE: Record<PostOperationRunStatus, Tone> = {
  IN_PROGRESS: 'pending',
  ATTENTION: 'serious',
  COMPLETED: 'ok',
  NOTHING_REQUIRED: 'neutral',
};

const STEP_TONE: Record<PostOperationStep['status'], Tone> = {
  WAITING: 'neutral',
  QUEUED: 'pending',
  REQUESTED: 'pending',
  ACCEPTED: 'info',
  IN_PROGRESS: 'info',
  DONE: 'ok',
  REJECTED: 'serious',
  FAILED: 'crit',
  NOT_SUPPORTED: 'warn',
  DISABLED: 'neutral',
  SUPERSEDED: 'neutral',
};

function isRunStatus(v: string | null): v is PostOperationRunStatus {
  return v !== null && (RUN_STATUSES as readonly string[]).includes(v);
}

/** One step: the action, its connector and where it is. */
function StepChip({ step }: { step: PostOperationStep }) {
  const { t } = useTranslation('compliance');
  return (
    <div className="flex flex-wrap items-center gap-1.5 text-xs">
      <span className="font-medium">{t(`postOps.action.${step.action}`)}</span>
      {step.connector ? <code className="text-muted-foreground">{step.connector}</code> : null}
      <Badge tone={STEP_TONE[step.status]}>{t(`postOps.stepStatus.${step.status}`)}</Badge>
      {step.detail ? <span className="text-muted-foreground">{step.detail}</span> : null}
    </div>
  );
}

/** What a "Chạy bù" pass found or did: counts per operation and the dossiers. */
function BackfillSummary({ report }: { report: BackfillReport }) {
  const { t } = useTranslation('compliance');
  return (
    <div className="grid gap-2 text-sm">
      <p>
        {report.dry_run
          ? t('postOps.backfill.found', { count: report.candidates, events: report.from_events, dossiers: report.from_dossiers })
          : t('postOps.backfill.done', { created: report.created, already: report.already })}
      </p>
      {Object.entries(report.by_operation).length ? (
        <p className="text-xs text-muted-foreground">
          {(Object.entries(report.by_operation) as [DomainOperation, number][])
            .map(([op, n]) => `${t(`postOps.operation.${op}`)}: ${n}`)
            .join(' · ')}
        </p>
      ) : null}
      {report.items.length ? (
        <ul className="max-h-48 overflow-auto rounded border p-2 text-xs">
          {report.items.map((i) => (
            <li key={i.dossier_id} className="flex flex-wrap justify-between gap-2">
              <span>{i.ascii_name}</span>
              <span className="text-muted-foreground">
                {t(`postOps.operation.${i.operation}`)} · {t(`postOps.backfill.source.${i.source}`)}
              </span>
            </li>
          ))}
        </ul>
      ) : null}
      {report.more ? <p className="text-xs text-muted-foreground">{t('postOps.backfill.more')}</p> : null}
    </div>
  );
}

/**
 * "Chạy bù" (ADR-024 remaining item 7): the .vn dossiers completed before the post-operation runs
 * existed get the run of their latest operation (registrar signature, then the HSTM sync). A dry
 * run shows the counts first; running it twice creates nothing twice.
 */
function BackfillForm({ onDone, onCancel }: { onDone: () => void; onCancel: () => void }) {
  const { t } = useTranslation('compliance');
  const { t: tc } = useTranslation();
  const [since, setSince] = useState('2025-01-01');
  const preview = useWrite((s: string, opts) => api.backfillPostOperations({ since: s, dry_run: true }, opts));
  const run = useWrite((s: string, opts) => api.backfillPostOperations({ since: s, dry_run: false }, opts));
  const previewed = preview.state.phase === 'done' ? preview.state.result : null;
  const ran = run.state.phase === 'done' ? run.state.result : null;
  return (
    <div className="grid gap-4">
      <p className="text-sm text-muted-foreground">{t('postOps.backfill.body')}</p>
      <Field label={t('postOps.backfill.since')} hint={t('postOps.backfill.sinceHint')}>
        <Input
          type="date"
          value={since}
          onChange={(e) => setSince(e.target.value)}
          disabled={run.busy || ran !== null}
          className="w-44"
        />
      </Field>
      {previewed && previewed.since === since && !ran ? <BackfillSummary report={previewed} /> : null}
      {ran ? (
        <SuccessNote>
          <BackfillSummary report={ran} />
        </SuccessNote>
      ) : null}
      <MfaHint permission="regulatory_report.manage" />
      {preview.state.phase === 'error' ? <WriteError error={preview.state.error} /> : null}
      {run.state.phase === 'error' ? <WriteError error={run.state.error} /> : null}
      <FormActions>
        <Button type="button" variant="outline" onClick={ran ? onDone : onCancel} disabled={run.busy}>
          {ran ? tc('action.close') : tc('action.cancel')}
        </Button>
        {ran ? null : (
          <>
            <Button type="button" variant="outline" disabled={!since || preview.busy || run.busy} onClick={() => void preview.run(since)}>
              <Search /> {t('postOps.backfill.preview')}
            </Button>
            <Button
              type="button"
              disabled={!previewed || previewed.since !== since || previewed.candidates === 0 || run.busy}
              onClick={() => void run.run(since)}
            >
              <Play /> {t('postOps.backfill.run', { count: previewed?.since === since ? previewed.candidates : 0 })}
            </Button>
          </>
        )}
      </FormActions>
    </div>
  );
}

/**
 * "Sau nghiệp vụ" (ADR-024): for every operation a registry completed, what the domain's TLD
 * required and where each step is — the registrar signature, the dossier sync to VNNIC HSTM
 * (.vn), the change report to VNNIC's international-domain reporting (other TLDs), or nothing.
 */
export function PostOperationsPage() {
  const { t } = useTranslation('compliance');
  const { t: tc } = useTranslation();
  useDocumentTitle(t('postOps.title'));
  const [params, setParams] = useSearchParams();
  const raw = params.get('status');
  const status = isRunStatus(raw) ? raw : undefined;
  const result = useApi((signal) => api.listPostOperations({ status, limit: 200 }, { signal }), [status]);
  const requirements = useApi((signal) => api.getComplianceRequirements({ signal }), []);
  const rows = result.status === 'success' ? result.data.items : [];
  const [backfill, setBackfill] = useState(false);
  const setStatus = (v: string | null) =>
    setParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        if (v) p.set('status', v);
        else p.delete('status');
        return p;
      },
      { replace: true },
    );

  const columns: Column<PostOperationRun>[] = [
    {
      key: 'domain',
      header: t('postOps.col.domain'),
      cell: (r) => <EntityRef label={r.ascii_name} to={`/domains/${encodeURIComponent(r.ascii_name)}`} id={r.domain_id} sub={r.requirement_group} />,
    },
    { key: 'op', header: t('postOps.col.operation'), cell: (r) => t(`postOps.operation.${r.operation}`) },
    { key: 'status', header: t('postOps.col.status'), cell: (r) => <Badge tone={RUN_TONE[r.status]}>{t(`postOps.runStatus.${r.status}`)}</Badge> },
    {
      key: 'steps',
      header: t('postOps.col.steps'),
      cell: (r) =>
        r.steps.length === 0 ? (
          <span className="text-xs text-muted-foreground">{t('postOps.nothing')}</span>
        ) : (
          <div className="grid gap-1">
            {r.steps.map((s) => (
              <StepChip key={s.id} step={s} />
            ))}
          </div>
        ),
    },
    { key: 'at', header: t('postOps.col.occurred'), cell: (r) => <When at={r.occurred_at} /> },
  ];

  return (
    <>
      <PageHeader
        title={t('postOps.title')}
        crumbs={[{ label: t('crumbs.compliance') }, { label: t('crumbs.vnnic') }]}
        subtitle={t('postOps.subtitle')}
        actions={
          <>
            <Button size="sm" variant="outline" onClick={result.reload} disabled={result.status === 'loading'}>
              <RefreshCw /> {tc('action.refresh')}
            </Button>
            <Button size="sm" variant="outline" onClick={() => setBackfill(true)}>
              <History /> {t('postOps.backfill.button')}
            </Button>
          </>
        }
      />
      <div className="space-y-6">
        <Callout tone="info" title={t('postOps.howTitle')}>
          <p>{t('postOps.how')}</p>
          {requirements.status === 'success' ? (
            <p className="text-xs text-muted-foreground">{t('postOps.version', { version: requirements.data.version })}</p>
          ) : null}
        </Callout>
        <Section flush>
          <FilterBar
            className="px-5"
            actions={
              status ? (
                <Button variant="ghost" size="sm" onClick={() => setStatus(null)}>
                  <RotateCcw /> {tc('action.reset')}
                </Button>
              ) : null
            }
          >
            <SelectField
              label={t('common.status')}
              value={status ?? ''}
              allLabel={t('postOps.allStatuses')}
              options={RUN_STATUSES.map((s) => ({ value: s, label: t(`postOps.runStatus.${s}`) }))}
              onChange={(v) => setStatus(v || null)}
              className="w-full sm:w-56"
            />
          </FilterBar>
          {result.status === 'loading' ? (
            <div className="px-5">
              <Loading label={t('postOps.loading')} rows={4} />
            </div>
          ) : null}
          {result.status === 'error' ? (
            <div className="px-5 pb-5">
              <ErrorState error={result.error} onRetry={result.reload} title={t('postOps.loadError')} />
            </div>
          ) : null}
          {result.status === 'success' ? (
            <DataTable columns={columns} rows={rows} rowKey={(r) => r.id} empty={<EmptyState title={t('postOps.empty')} />} />
          ) : null}
        </Section>
      </div>
      <ActionDialog open={backfill} onOpenChange={(o) => setBackfill(o)} title={t('postOps.backfill.title')}>
        {backfill ? (
          <BackfillForm
            onCancel={() => setBackfill(false)}
            onDone={() => {
              setBackfill(false);
              result.reload();
            }}
          />
        ) : null}
      </ActionDialog>
    </>
  );
}
